import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  type Timestamp,
} from 'firebase/firestore'
import { db } from './firebase'
import { WORK_TYPES_SEED, type WorkTypeSeed } from '../data/workTypesSeed'
import {
  inferWorkTypeLevelFromName,
  parseWorkTypeLevel,
  type WorkTypeLevel,
} from '../utils/workTypeLevels'

export interface WorkType {
  id: string
  name: string
  /** One or more Team Brevity team names. */
  teams: string[]
  /** Firestore `workTypeCategories` doc id; null = Uncategorised. */
  categoryId: string | null
  /** Automation / delivery maturity level (1–4). */
  level: WorkTypeLevel
  /** Legacy Pipedrive product category label (seed source). */
  pipedriveCategory: string | null
  sourceProductIds: number[]
  createdAt?: Timestamp
  updatedAt?: Timestamp
}

const COLLECTION = 'workTypes'
const BATCH_LIMIT = 400

export function normalizeWorkTypeTeams(data: {
  teams?: unknown
  team?: unknown
}): string[] {
  if (Array.isArray(data.teams)) {
    const names = data.teams
      .map((t) => String(t || '').trim())
      .filter(Boolean)
    if (names.length > 0) return [...new Set(names)]
  }
  const legacy = String(data.team || '').trim()
  return legacy ? [legacy] : []
}

export class WorkTypesService {
  private static instance: WorkTypesService

  static getInstance(): WorkTypesService {
    if (!WorkTypesService.instance) {
      WorkTypesService.instance = new WorkTypesService()
    }
    return WorkTypesService.instance
  }

  async getAllWorkTypes(): Promise<WorkType[]> {
    const snapshot = await getDocs(
      query(collection(db, COLLECTION), orderBy('name'))
    )
    return snapshot.docs.map((d) => {
      const data = d.data()
      const name = String(data.name || '')
      return {
        id: d.id,
        name,
        teams: normalizeWorkTypeTeams(data),
        categoryId:
          data.categoryId == null || data.categoryId === ''
            ? null
            : String(data.categoryId),
        level: parseWorkTypeLevel(data.level, name),
        pipedriveCategory:
          data.pipedriveCategory == null ? null : String(data.pipedriveCategory),
        sourceProductIds: Array.isArray(data.sourceProductIds)
          ? data.sourceProductIds.map(Number)
          : [],
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      } satisfies WorkType
    })
  }

  async countWorkTypes(): Promise<number> {
    const snapshot = await getDocs(collection(db, COLLECTION))
    return snapshot.size
  }

  async createWorkType(input: {
    name: string
    teams: string[]
    categoryId: string | null
    level: WorkTypeLevel
  }): Promise<string> {
    const teams = [...new Set(input.teams.map((t) => t.trim()).filter(Boolean))]
    if (teams.length === 0) {
      throw new Error('At least one team is required')
    }
    const ref = doc(collection(db, COLLECTION))
    await setDoc(ref, {
      name: input.name.trim(),
      teams,
      team: teams[0],
      categoryId: input.categoryId,
      level: input.level,
      pipedriveCategory: null,
      sourceProductIds: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return ref.id
  }

  async updateWorkType(
    workTypeId: string,
    updates: {
      name?: string
      teams?: string[]
      categoryId?: string | null
      level?: WorkTypeLevel
    }
  ): Promise<void> {
    const payload: {
      updatedAt: ReturnType<typeof serverTimestamp>
      name?: string
      teams?: string[]
      team?: string
      categoryId?: string | null
      level?: WorkTypeLevel
    } = {
      updatedAt: serverTimestamp(),
    }
    if (updates.name !== undefined) payload.name = updates.name.trim()
    if (updates.teams !== undefined) {
      const teams = [...new Set(updates.teams.map((t) => t.trim()).filter(Boolean))]
      if (teams.length === 0) {
        throw new Error('At least one team is required')
      }
      payload.teams = teams
      payload.team = teams[0]
    }
    if (updates.categoryId !== undefined) payload.categoryId = updates.categoryId
    if (updates.level !== undefined) payload.level = updates.level
    await updateDoc(doc(db, COLLECTION, workTypeId), payload)
  }

  async deleteWorkType(workTypeId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, workTypeId))
  }

  /**
   * Ensure every work type has a `teams` array (migrating legacy `team` string).
   */
  async backfillTeamsArray(): Promise<{ updated: number }> {
    const snapshot = await getDocs(collection(db, COLLECTION))
    const toUpdate = snapshot.docs.filter((d) => {
      const data = d.data()
      return !Array.isArray(data.teams) || data.teams.length === 0
    })

    let updated = 0
    for (let i = 0; i < toUpdate.length; i += BATCH_LIMIT) {
      const chunk = toUpdate.slice(i, i + BATCH_LIMIT)
      const batch = writeBatch(db)
      for (const d of chunk) {
        const teams = normalizeWorkTypeTeams(d.data())
        if (teams.length === 0) continue
        batch.update(d.ref, {
          teams,
          team: teams[0],
          updatedAt: serverTimestamp(),
        })
        updated++
      }
      await batch.commit()
    }
    return { updated }
  }

  /**
   * Backfill `level` on work types missing it.
   * Rule: name contains “Bespoke” → 1, otherwise → 2.
   */
  async backfillLevelsFromNames(): Promise<{ updated: number }> {
    const snapshot = await getDocs(collection(db, COLLECTION))
    const missing = snapshot.docs.filter((d) => {
      const level = d.data().level
      return !(level === 1 || level === 2 || level === 3 || level === 4)
    })

    let updated = 0
    for (let i = 0; i < missing.length; i += BATCH_LIMIT) {
      const chunk = missing.slice(i, i + BATCH_LIMIT)
      const batch = writeBatch(db)
      for (const d of chunk) {
        const name = String(d.data().name || '')
        batch.update(d.ref, {
          level: inferWorkTypeLevelFromName(name),
          updatedAt: serverTimestamp(),
        })
        updated++
      }
      await batch.commit()
    }
    return { updated }
  }

  /**
   * Upsert the Pipedrive-seeded catalog into `workTypes`.
   * Uses stable document ids from the seed so re-runs are idempotent.
   */
  async seedFromPipedriveCatalog(
    seeds: WorkTypeSeed[] = WORK_TYPES_SEED
  ): Promise<{ written: number }> {
    let written = 0
    for (let i = 0; i < seeds.length; i += BATCH_LIMIT) {
      const chunk = seeds.slice(i, i + BATCH_LIMIT)
      const batch = writeBatch(db)
      for (const seed of chunk) {
        const ref = doc(db, COLLECTION, seed.id)
        const teams = [seed.team]
        batch.set(
          ref,
          {
            name: seed.name,
            team: seed.team,
            teams,
            pipedriveCategory: seed.pipedriveCategory,
            sourceProductIds: seed.sourceProductIds,
            level: inferWorkTypeLevelFromName(seed.name),
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        )
        written++
      }
      await batch.commit()
    }
    return { written }
  }
}
